import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Keep a single in-flight refresh promise so concurrent 401s only fire one
// /auth/refresh call.
let refreshInFlight = null;
const NEVER_REFRESH = new Set(["/auth/refresh", "/auth/login", "/auth/register"]);
// Failures here are normal for anonymous users — don't show "session expired".
const SILENT_401 = new Set(["/auth/me"]);

const callRefresh = () => {
  if (!refreshInFlight) {
    refreshInFlight = axios
      .post(`${API}/auth/refresh`, null, { withCredentials: true })
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
};

// Global response interceptor.
// - 401 → try one transparent refresh, then retry the original request.
//          If refresh also 401s, surface a friendly "session expired" toast
//          (except for /auth/me which is the implicit session probe).
// - 402 → Reads+ paywall toast.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config || {};
    const path = (original.url || "").replace(API, "");

    if (status === 401 && !original.__isRetry && !NEVER_REFRESH.has(path)) {
      try {
        await callRefresh();
        original.__isRetry = true;
        return api.request(original);
      } catch {
        if (!SILENT_401.has(path)) {
          toast.error("Session expired — please log in again.", {
            action: {
              label: "Log in",
              onClick: () => { window.location.href = "/login"; },
            },
            duration: 6000,
          });
        }
      }
    }

    if (status === 402) {
      const detail = error.response.data?.detail || "Upgrade to Reads+ to continue.";
      toast.message("Reads+ required", {
        description: typeof detail === "string" ? detail : "Upgrade to Reads+ to continue.",
        action: {
          label: "Upgrade",
          onClick: () => { window.location.href = "/subscribe"; },
        },
        duration: 8000,
      });
    }

    // Freemium OR-trigger. The backend returns 403 with MONTHLY_LIMIT_EXCEEDED
    // when a free user hits their monthly cap. A global listener fires an
    // upgrade toast — see components/FreemiumGate.jsx.
    if (status === 403) {
      const detail = error.response.data?.detail;
      if (detail === "MONTHLY_LIMIT_EXCEEDED") {
        window.dispatchEvent(new CustomEvent("freemium:monthly-limit"));
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
